import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Plus, Package, Trash2, ImagePlus, X, Pencil } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState } from "@/components/admin/ui";
import { centsToDisplay, dollarsInputToCents } from "@/lib/money";
import { ProductCoverThumb } from "@/components/org/ProductCoverThumb";
import { ProductEditModal } from "@/components/org/ProductEditModal";
import { ImageStyleSelector } from "@/components/org/ImageStyleSelector";
import { usePolling } from "@/hooks/usePolling";
import { LastUpdated } from "@/components/common/LastUpdated";
import { AddingProgressBar } from "@/components/common/AddingProgressBar";

const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_BYTES = 10 * 1024 * 1024;

export default function OrgProducts() {
  const { API, user } = useAuth();
  const isOwner = user?.role === "org_owner";
  const isManager = isOwner || user?.role === "org_admin";
  const canManageImages = isOwner || user?.role === "org_admin" || (user?.role !== "team_lead" && !!user?.catalog_manage);
  const [products, setProducts] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const successTimer = useRef(null);
  const [form, setForm] = useState({ name: "", category: "", sku: "", price: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [editingProductId, setEditingProductId] = useState(null);
  const [stagedFile, setStagedFile] = useState(null);
  const [stagedPreview, setStagedPreview] = useState(null);
  const [imageStyle, setImageStyle] = useState("flat");
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  const fileInputRef = useRef(null);
  const styleUserChangedRef = useRef(false);

  const load = useCallback(async (silent) => {
    try {
      const { data } = await axios.get(`${API}/org/products`, { withCredentials: true });
      setProducts(data.products || []);
      setLastUpdated(Date.now());
    } catch (err) {
      if (!silent) setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/org/image-styles`, { withCredentials: true })
      .then(({ data }) => { if (!styleUserChangedRef.current) setImageStyle(data.product_image_style || "flat"); })
      .catch(() => {});
  }, [API]);

  const hasPendingImages = (products || []).some((p) => p.image_generation_status === "pending");

  // Skip the silent auto-refresh while adding a product or a row action is in flight.
  // Poll faster while any product is generating its AI image so placeholders resolve quickly.
  usePolling(() => {
    if (saving || savingId) return;
    load(true);
  }, hasPendingImages ? 3000 : 10000);

  const pickFile = (file) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) { setError("Please choose a PNG, JPEG, or WEBP image."); return; }
    if (file.size > MAX_BYTES) { setError("Image must be smaller than 10MB."); return; }
    setError("");
    setStagedFile(file);
    setStagedPreview(URL.createObjectURL(file));
  };

  const clearStagedFile = () => {
    setStagedFile(null);
    setStagedPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const addProduct = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/org/products`, {
        name: form.name,
        description: form.description || null,
        category: form.category || null,
        sku: form.sku || null,
        price_cents: form.price ? dollarsInputToCents(form.price) : null,
        active: true,
        skip_ai_image: !!stagedFile,
      }, { withCredentials: true, timeout: 20000 });
      if (stagedFile) {
        const fd = new FormData();
        fd.append("file", stagedFile);
        await axios.post(`${API}/org/products/${data.product.product_id}/images`, fd, { withCredentials: true });
      }
      setForm({ name: "", category: "", sku: "", price: "", description: "" });
      clearStagedFile();
      await load();
      setSuccess(`"${data.product.name}" added${stagedFile ? "." : " — generating its image now."}`);
      clearTimeout(successTimer.current);
      successTimer.current = setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (product) => {
    setSavingId(product.product_id);
    try {
      await axios.patch(`${API}/org/products/${product.product_id}`, { active: !product.active }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const removeProduct = async (product) => {
    setSavingId(product.product_id);
    setError("");
    try {
      await axios.delete(`${API}/org/products/${product.product_id}`, { withCredentials: true });
      setConfirmDeleteId(null);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const updateProductInList = (updatedProduct) => {
    setProducts((prev) => prev.map((p) => (p.product_id === updatedProduct.product_id ? updatedProduct : p)));
  };

  const changeImageStyle = async (newStyle) => {
    const previous = imageStyle;
    styleUserChangedRef.current = true;
    setImageStyle(newStyle);
    try {
      await axios.patch(`${API}/org/image-styles`, { catalog: "product", style: newStyle }, { withCredentials: true });
    } catch (err) {
      setImageStyle(previous);
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const regenerateAll = async () => {
    setRegeneratingAll(true);
    setError("");
    try {
      await axios.post(`${API}/org/products/images/regenerate-all`, {}, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRegeneratingAll(false);
    }
  };

  const hasAiImages = (products || []).some((p) => (p.images || []).some((img) => img.source === "ai_generated"));
  const editingProduct = (products || []).find((p) => p.product_id === editingProductId) || null;
  const canOpenEdit = isOwner || canManageImages;

  return (
    <div className="space-y-6" data-testid="org-products-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-950 dark:text-white">Products</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Physical or packaged goods your organization sells, separate from Services.</p>
        </div>
        <LastUpdated timestamp={lastUpdated} testId="org-products-last-updated" />
      </div>

      {isManager && (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[3fr_2fr] lg:items-start">
      <Panel title="Your Products">
        {products === null ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400">Loading products…</div>
        ) : products.length === 0 ? (
          <EmptyState icon={Package} title="No products yet." message="Add your first product to get started." testId="org-products-empty" />
        ) : (
          <div className="space-y-2" data-testid="org-products-list">
            {products.map((p) => (
              <div key={p.product_id} className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid={`org-product-row-${p.product_id}`}>
                <button
                  type="button"
                  onClick={() => canOpenEdit && setEditingProductId(p.product_id)}
                  disabled={!canOpenEdit}
                  data-testid={`org-product-open-edit-${p.product_id}`}
                  className={canOpenEdit ? "cursor-pointer" : "cursor-default"}
                >
                  <ProductCoverThumb product={p} testId={`org-product-thumb-${p.product_id}`} />
                </button>
                <div className="min-w-[160px] flex-1">
                  <p className="text-sm font-bold text-slate-950 dark:text-white">{p.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {p.category ? `${p.category} · ` : ""}{p.sku ? `SKU ${p.sku} · ` : ""}{p.price_cents != null ? centsToDisplay(p.price_cents) : "No price set"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {canOpenEdit && (
                    <button
                      type="button"
                      onClick={() => setEditingProductId(p.product_id)}
                      data-testid={`org-product-edit-btn-${p.product_id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => toggleActive(p)}
                    disabled={savingId === p.product_id}
                    data-testid={`org-product-toggle-${p.product_id}`}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${p.active ? "border-emerald-200 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-400 dark:hover:bg-emerald-500/10" : "border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-white/15 dark:text-slate-400 dark:hover:bg-white/10"}`}
                  >
                    {p.active ? "Active" : "Inactive"}
                  </button>
                  {isManager && (
                    confirmDeleteId === p.product_id ? (
                      <div className="flex items-center gap-1.5" data-testid={`org-product-confirm-delete-${p.product_id}`}>
                        <button type="button" onClick={() => removeProduct(p)} disabled={savingId === p.product_id} data-testid={`org-product-confirm-delete-yes-${p.product_id}`} className="rounded-lg bg-red-600 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50">Delete?</button>
                        <button type="button" onClick={() => setConfirmDeleteId(null)} data-testid={`org-product-confirm-delete-cancel-${p.product_id}`} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-500 dark:border-white/15 dark:text-slate-400">Cancel</button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => setConfirmDeleteId(p.product_id)} disabled={savingId === p.product_id} data-testid={`org-product-delete-${p.product_id}`} className="rounded-lg border border-red-200 p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10">
                        <Trash2 size={14} />
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel
        title="Add a Product"
        action={
          <ImageStyleSelector
            testIdPrefix="org-products"
            style={imageStyle}
            onChangeStyle={changeImageStyle}
            onRegenerateAll={regenerateAll}
            regenerating={regeneratingAll}
            hasAiImages={hasAiImages}
          />
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Product Name *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="org-product-name-input" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Category</label>
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="org-product-category-input" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">SKU</label>
            <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="org-product-sku-input" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Price ($)</label>
            <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="org-product-price-input" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="org-product-description-input" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Product Image (optional)</label>
            <div className="mt-1 flex items-center gap-3">
              {stagedPreview ? (
                <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-slate-200 dark:border-white/15">
                  <img src={stagedPreview} alt="Selected preview" className="h-full w-full object-cover" data-testid="org-product-create-image-preview" />
                  <button type="button" onClick={clearStagedFile} data-testid="org-product-create-image-remove-btn" className="absolute right-1 top-1 rounded-full bg-slate-950/70 p-0.5 text-white">
                    <X size={10} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="org-product-create-image-btn"
                  className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border border-dashed border-slate-300 text-slate-400 hover:bg-slate-50 dark:border-white/15 dark:text-slate-500 dark:hover:bg-white/10"
                >
                  <ImagePlus size={18} />
                </button>
              )}
              <input ref={fileInputRef} type="file" accept={ALLOWED_TYPES.join(",")} className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} data-testid="org-product-create-image-input" />
              <p className="text-xs text-slate-400 dark:text-slate-500">Upload a photo, or leave blank and Nova will generate one automatically.</p>
            </div>
          </div>
        </div>
        {error && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-products-error">{error}</p>}
        {success && <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400" data-testid="org-products-success">{success}</p>}
        <button type="button" onClick={addProduct} disabled={saving || !form.name} data-testid="org-product-add-btn" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">
          <Plus size={16} /> {saving ? "Adding…" : "Add Product"}
        </button>
        <AddingProgressBar active={saving} label="Adding product…" testId="org-product-adding-progress" />
      </Panel>
      </div>
      )}

      {!isManager && (
      <Panel title="Your Products">
        {products === null ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400">Loading products…</div>
        ) : products.length === 0 ? (
          <EmptyState icon={Package} title="No products yet." message="Ask your organization owner to add one." testId="org-products-empty" />
        ) : (
          <div className="space-y-2" data-testid="org-products-list">
            {products.map((p) => (
              <div key={p.product_id} className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid={`org-product-row-${p.product_id}`}>
                <button
                  type="button"
                  onClick={() => canOpenEdit && setEditingProductId(p.product_id)}
                  disabled={!canOpenEdit}
                  data-testid={`org-product-open-edit-${p.product_id}`}
                  className={canOpenEdit ? "cursor-pointer" : "cursor-default"}
                >
                  <ProductCoverThumb product={p} testId={`org-product-thumb-${p.product_id}`} />
                </button>
                <div className="min-w-[160px] flex-1">
                  <p className="text-sm font-bold text-slate-950 dark:text-white">{p.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {p.category ? `${p.category} · ` : ""}{p.sku ? `SKU ${p.sku} · ` : ""}{p.price_cents != null ? centsToDisplay(p.price_cents) : "No price set"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {canOpenEdit && (
                    <button
                      type="button"
                      onClick={() => setEditingProductId(p.product_id)}
                      data-testid={`org-product-edit-btn-${p.product_id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  )}
                  <span data-testid={`org-product-status-${p.product_id}`} className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${p.active ? "border-emerald-200 text-emerald-600 dark:border-emerald-500/30 dark:text-emerald-400" : "border-slate-200 text-slate-500 dark:border-white/15 dark:text-slate-400"}`}>
                    {p.active ? "Active" : "Inactive"}
                  </span>
                  {isManager && (
                    confirmDeleteId === p.product_id ? (
                      <div className="flex items-center gap-1.5" data-testid={`org-product-confirm-delete-${p.product_id}`}>
                        <button type="button" onClick={() => removeProduct(p)} disabled={savingId === p.product_id} data-testid={`org-product-confirm-delete-yes-${p.product_id}`} className="rounded-lg bg-red-600 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50">Delete?</button>
                        <button type="button" onClick={() => setConfirmDeleteId(null)} data-testid={`org-product-confirm-delete-cancel-${p.product_id}`} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-500 dark:border-white/15 dark:text-slate-400">Cancel</button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => setConfirmDeleteId(p.product_id)} disabled={savingId === p.product_id} data-testid={`org-product-delete-${p.product_id}`} className="rounded-lg border border-red-200 p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10">
                        <Trash2 size={14} />
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
      )}

      {editingProduct && (
        <ProductEditModal
          product={editingProduct}
          canEdit={isManager}
          canManageImages={canManageImages}
          onClose={() => setEditingProductId(null)}
          onUpdated={updateProductInList}
          onRefreshNow={() => load(true)}
        />
      )}
    </div>
  );
}
